















































































































































































































































































































import { Component, Watch } from 'vue-property-decorator';
import GiganautVueDialog from '@/components/Shared/GiganautVueDialog';
import PageNames from '@/router/PageNames';
import CreateOpportunityRequest from '@/models/Opportunities/CreateOpportunityRequest';
import { OPPORTUNITY_SOURCE_CODE_CAMPAIGN } from '@/models/Opportunities/OpportunitySourceDto';
import { OpportunityDetailsModule } from '@/store/modules/opportunityDetails';
import { OpportunitiesListModule } from '@/store/modules/opportunitiesList';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import { CompanyListModule } from '@/store/modules/companyList';
import { CampaignListModule } from '@/store/modules/campaignList';
import { AppModule } from '@/store/modules/app';
import ErrorMessage from '@/models/Shared/ErrorMessage';
import ajax from '@/lib/ajax';
import ListUtils from '@/lib/listUtils';
import CampaignStatus from '@/models/Campaigns/CampaignStatus';

@Component({ name: 'OpportunityCreate' })
export default class extends GiganautVueDialog {
    public entity = new CreateOpportunityRequest();

    selectedOwner: any = null;
    selectedBranch: any = null;
    selectedDesk: any = null;
    selectedCompany: any = null;
    selectedLocation: any = null;
    selectedContact: any = null;

    // Locations and contacts are scoped to the selected company. They re-fetch
    // whenever selectedCompany changes; cleared when company is cleared.
    companyLocations: any[] = [];
    companyContacts: any[] = [];
    loadingCompanyLinks = false;

    includeClosedCampaigns = false;

    get saving() {
        return OpportunityDetailsModule.saving;
    }

    get opportunitySources() { return AgencySettingsModule.opportunitySources; }

    get locationTypes() { return AgencySettingsModule.locationTypes; }

    // Venue type for a linked location — prefer the preloaded object, else
    // resolve the id against the loaded location-type lookup.
    get linkedVenueTypeName(): string {
        if (!this.selectedLocation) return '';
        if (this.selectedLocation.locationType) return this.selectedLocation.locationType.name;
        const t = this.locationTypes.find((lt: any) => lt.id === this.selectedLocation.locationTypeId);
        return t ? t.name : '';
    }

    get agencyUsers() { return AgencySettingsModule.agencyUsers; }
    get branches() { return AgencySettingsModule.branches; }
    get desks() {
        if (!this.selectedBranch) return [];
        const branch = AgencySettingsModule.branches.find((b: any) => b.id === this.selectedBranch.id);
        return branch ? branch.desks : [];
    }
    get companies() { return CompanyListModule.companies; }
    get campaigns() { return CampaignListModule.campaigns; }
    get loadingCampaigns() { return CampaignListModule.loading; }

    get workTypeOptions() {
        return ListUtils.workTypes();
    }

    get valueTypeOptions() {
        // Mirrors OPPORTUNITY_VALUE_TYPE_* constants in api/core/opportunities.go.
        return [
            { value: 1, label: 'One Off' },
        ];
    }

    // The Campaign field is only meaningful when the selected source is the
    // seeded 'Campaign' system row. Each agency's row has its own UUID so
    // we identify it by isSystem + code (see OPPORTUNITY_SOURCE_CODE_CAMPAIGN
    // in OpportunitySourceDto.ts).
    get isCampaignSource(): boolean {
        const src = this.opportunitySources.find((s: any) => s.id === this.entity.sourceId);
        return !!src && src.isSystem && src.code === OPPORTUNITY_SOURCE_CODE_CAMPAIGN;
    }

    // Cross-field validation: mirrors CreateOpportunityRequest.Validate() on the
    // backend. An opportunity must anchor to at least one of company / location /
    // contact — either an existing record or enough prospect-snapshot data.
    // Contact snapshot needs BOTH first and last name.
    get linkOrSnapshotError(): string {
        const hasCompany = !!this.selectedCompany || !!this.entity.prospectCompanyName.trim();
        const hasLocation = !!this.selectedLocation || !!this.entity.prospectLocationName.trim();
        const hasContact = !!this.selectedContact ||
            (!!this.entity.prospectContactFirstName.trim() && !!this.entity.prospectContactLastName.trim());
        if (hasCompany || hasLocation || hasContact) return '';
        const company = (this.Labels.company || 'company').toLowerCase();
        const location = (this.Labels.location || 'location').toLowerCase();
        return `Add at least one of: an existing ${company}, ${location} or contact, `
            + `or a prospect ${company} name, ${location} name, or contact first and last name.`;
    }

    ownerLabel(item: any) {
        return item.person ? `${item.person.firstName} ${item.person.lastName}` : '';
    }

    contactLabel(item: any) {
        return item.person ? `${item.person.firstName} ${item.person.lastName}` : '';
    }

    // Display helpers — when an existing record is picked, surface its field
    // value (read-only); otherwise expose the editable prospect-snapshot value
    // bound to entity.prospect*.
    companyDisplay(linkedKey: string, prospectKey: string): string {
        if (this.selectedCompany) return this.selectedCompany[linkedKey] || '';
        return (this.entity as any)[prospectKey] || '';
    }

    locationDisplay(linkedKey: string, prospectKey: string): string {
        if (this.selectedLocation) return this.selectedLocation[linkedKey] || '';
        return (this.entity as any)[prospectKey] || '';
    }

    contactDisplay(linkedKey: string, prospectKey: string): string {
        if (this.selectedContact) return this.selectedContact[linkedKey] || '';
        return (this.entity as any)[prospectKey] || '';
    }

    setProspect(key: string, value: string) {
        (this.entity as any)[key] = value || '';
    }

    // Concatenated single-line read-only address shown when a real Location is linked.
    get linkedLocationAddress(): string {
        if (!this.selectedLocation) return '';
        const loc = this.selectedLocation;
        const parts = [
            loc.buildingNameNumber,
            loc.streetRoadBlock,
            loc.cityTown,
            loc.stateProvinceRegion,
            loc.postZipCode,
            loc.countryCode,
        ].filter((p: string) => !!p);
        return parts.join(', ');
    }

    @Watch('dialog')
    async onDialogChanged(val: boolean) {
        if (val) {
            this.entity = new CreateOpportunityRequest();
            this.selectedOwner = null;
            this.selectedBranch = null;
            this.selectedDesk = null;
            this.selectedCompany = null;
            this.selectedLocation = null;
            this.selectedContact = null;
            this.companyLocations = [];
            this.companyContacts = [];
            // Pre-load reference data the form needs.
            if (AgencySettingsModule.agencyUsers.length === 0) {
                AgencySettingsModule.getAgencyUsers();
            }
            if (AgencySettingsModule.branches.length === 0) {
                await AgencySettingsModule.getBranches();
            }
            if (AgencySettingsModule.opportunitySources.length === 0) {
                AgencySettingsModule.getOpportunitySources();
            }
            if (AgencySettingsModule.locationTypes.length === 0) {
                AgencySettingsModule.getLocationTypes();
            }
            if (CompanyListModule.companies.length === 0) {
                CompanyListModule.searchCompanies();
            }
            this.fetchCampaigns();
        }
    }

    @Watch('selectedBranch')
    onBranchChanged() {
        this.selectedDesk = null;
    }

    @Watch('includeClosedCampaigns')
    onIncludeClosedCampaignsChanged() {
        this.fetchCampaigns();
    }

    fetchCampaigns() {
        const statuses = this.includeClosedCampaigns
            ? [CampaignStatus.Active, CampaignStatus.Closed]
            : [CampaignStatus.Active];
        CampaignListModule.fetchCampaignsByStatuses(statuses);
    }

    @Watch('entity.sourceId')
    onSourceChanged() {
        // Clear any previously-picked campaign when the source moves off
        // 'Campaign' — the field disappears so the linked value would be
        // an invisible orphan otherwise.
        if (!this.isCampaignSource) {
            this.entity.campaignId = null;
        }
    }

    @Watch('selectedCompany')
    async onCompanyChanged(newCompany: any, oldCompany: any) {
        // Always clear downstream selections when the company changes.
        this.selectedLocation = null;
        this.selectedContact = null;
        if (!newCompany) {
            this.companyLocations = [];
            this.companyContacts = [];
            return;
        }
        // Linked an existing company — the prospect company snapshot is not used,
        // so clear it out (the linked company's details are shown read-only).
        this.clearProspectCompanyFields();
        // Same company picked again — do nothing (avoids a useless refetch).
        if (oldCompany && oldCompany.id === newCompany.id) {
            return;
        }
        await this.loadCompanyLinks(newCompany.id);
    }

    @Watch('selectedLocation')
    onSelectedLocationChanged(location: any) {
        // Linked an existing location — clear the prospect location snapshot.
        if (location) {
            this.clearProspectLocationFields();
        }
    }

    @Watch('selectedContact')
    onSelectedContactChanged(contact: any) {
        // Linked an existing contact — clear the prospect contact snapshot.
        if (contact) {
            this.clearProspectContactFields();
        }
    }

    clearProspectCompanyFields() {
        this.entity.prospectCompanyName = '';
        this.entity.prospectCompanyEmail = '';
        this.entity.prospectCompanyPhone = '';
        this.entity.prospectCompanyWebsite = '';
        this.entity.prospectCompanyTaxNumber = '';
        this.entity.prospectCompanyRegistrationNumber = '';
        this.entity.prospectCompanyAddressType = '';
        this.entity.prospectCompanyAddressBuilding = '';
        this.entity.prospectCompanyAddressStreet = '';
        this.entity.prospectCompanyAddressLocality = '';
        this.entity.prospectCompanyAddressCity = '';
        this.entity.prospectCompanyAddressRegion = '';
        this.entity.prospectCompanyAddressPostcode = '';
        this.entity.prospectCompanyAddressCountry = '';
        this.entity.prospectCompanyAddressLatitude = null;
        this.entity.prospectCompanyAddressLongitude = null;
    }

    clearProspectLocationFields() {
        this.entity.prospectLocationName = '';
        this.entity.prospectLocationEmail = '';
        this.entity.prospectLocationPhone = '';
        this.entity.prospectLocationAddress1 = '';
        this.entity.prospectLocationAddress2 = '';
        this.entity.prospectLocationLocality = '';
        this.entity.prospectLocationCity = '';
        this.entity.prospectLocationRegion = '';
        this.entity.prospectLocationPostcode = '';
        this.entity.prospectLocationCountry = '';
        this.entity.prospectLocationTypeId = null;
        this.entity.prospectLocationLatitude = null;
        this.entity.prospectLocationLongitude = null;
    }

    clearProspectContactFields() {
        this.entity.prospectContactFirstName = '';
        this.entity.prospectContactLastName = '';
        this.entity.prospectContactEmail = '';
        this.entity.prospectContactMobilePhoneNumber = '';
        this.entity.prospectContactOtherPhoneNumber = '';
        this.entity.prospectContactJobTitle = '';
        this.entity.prospectContactPositionId = null;
        this.entity.prospectContactIsAgreedToMarketing = true;
    }

    async loadCompanyLinks(companyId: string) {
        this.loadingCompanyLinks = true;
        try {
            const [locationsRes, contactsRes] = await Promise.all([
                ajax.get(`/api/v1/agency/companies/${companyId}/locations`),
                ajax.get(`/api/v1/agency/companies/${companyId}/contacts`),
            ]);
            this.companyLocations = locationsRes.data || [];
            this.companyContacts = contactsRes.data || [];
        } catch (error) {
            AppModule.handleApiError(error);
            this.companyLocations = [];
            this.companyContacts = [];
        } finally {
            this.loadingCompanyLinks = false;
        }
    }

    async save() {
        if (!(this.$refs.form as any).validate()) {
            return;
        }
        if (this.linkOrSnapshotError) {
            // No inline banner any more — surface the cross-field rule as a
            // transient error so the blocked save isn't silent.
            AppModule.updateErrorMessage(new ErrorMessage(this.linkOrSnapshotError, 'OPPORTUNITY_LINK_REQUIRED', false));
            return;
        }
        this.entity.ownerId = this.selectedOwner ? this.selectedOwner.id : null;
        this.entity.branchId = this.selectedBranch ? this.selectedBranch.id : null;
        this.entity.deskId = this.selectedDesk ? this.selectedDesk.id : null;
        this.entity.companyId = this.selectedCompany ? this.selectedCompany.id : null;
        this.entity.locationId = this.selectedLocation ? this.selectedLocation.id : null;
        this.entity.contactId = this.selectedContact ? this.selectedContact.id : null;

        const opp = await OpportunityDetailsModule.create(this.entity);
        if (opp) {
            this.dialog = false;
            await OpportunitiesListModule.fetch();
            this.$router.push({ name: PageNames.AgencyOpportunityDetails, params: { opportunityId: opp.id } });
        }
    }
}
